import styles from './NextInvoiceCard.module.scss'

import { NextInvoiceResponse } from '@/api/billing'
import { Spinner } from '@/components/Spinner'
import { usePricing } from '@/hooks/usePricing'
import { BillingInfoCard } from '@/pages/app/Settings/components/BillingInfoCard/BillingInfoCard'
import { formatMinorUnitsAsCurrency } from '@/utils/currency'

interface NextInvoiceCardProps {
  nextInvoice: NextInvoiceResponse | null
  isLoading: boolean
}

export const NextInvoiceCard = ({ nextInvoice, isLoading }: NextInvoiceCardProps) => {
  // `locale` (viewer-appropriate) drives date/number formatting; the amount's
  // CURRENCY comes from the invoice itself, not the viewer's IP.
  const { locale } = usePricing()

  const formatDate = (date: Date | string): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    return dateObj.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    })
  }

  const renewalAmount = nextInvoice?.renewal_amount || 0
  const newSeatsAmount = nextInvoice?.new_seats_amount || 0
  const hasUpcomingCharges = renewalAmount > 0 || newSeatsAmount > 0 // Renewal or proration charges

  // The invoice's real billed currency. The backend derives it from the Stripe
  // upcoming invoice / the proration invoice rows, so it's present whenever there
  // are charges; USD is only a last-resort default for a null with no charges.
  const invoiceCurrency = nextInvoice?.currency || 'USD'
  const formatAmount = (minorUnits: number): string =>
    formatMinorUnitsAsCurrency(minorUnits, invoiceCurrency, { locale })

  const formattedChargeDate = nextInvoice?.charge_on ? formatDate(nextInvoice.charge_on) : undefined
  const formattedRenewalDate = nextInvoice?.renewal_date ? formatDate(nextInvoice.renewal_date) : undefined
  const formattedRenewalAmount = formatAmount(renewalAmount)
  const formattedNewSeatsAmount = formatAmount(newSeatsAmount)

  return (
    <BillingInfoCard.Root>
      <BillingInfoCard.Header>
        <BillingInfoCard.Title>Upcoming charges</BillingInfoCard.Title>
      </BillingInfoCard.Header>
      {isLoading ? (
        <div className={styles.loadingContainer}>
          <Spinner size={24} color="var(--neutral-600)" />
        </div>
      ) : !hasUpcomingCharges ? (
        <BillingInfoCard.Body>No upcoming charges.</BillingInfoCard.Body>
      ) : (
        <>
          <div className={styles.invoiceBreakdown}>
            {renewalAmount > 0 && (
              <>
                <div className={styles.breakdownRow}>
                  <span className={styles.breakdownLabel}>Renewal cost</span>
                  <span className={styles.breakdownAmount}>{formattedRenewalAmount}</span>
                </div>
                {formattedRenewalDate && (
                  <div className={styles.breakdownRow}>
                    <span className={styles.breakdownLabel}>Renewal date</span>
                    <span className={styles.breakdownAmount}>{formattedRenewalDate}</span>
                  </div>
                )}
              </>
            )}
            {renewalAmount > 0 && newSeatsAmount > 0 && <div className={styles.divider} />}
            {newSeatsAmount > 0 && (
              <>
                <div className={styles.breakdownRow}>
                  <span className={styles.breakdownLabel}>New seats (prorated)</span>
                  <span className={styles.breakdownAmount}>{formattedNewSeatsAmount}</span>
                </div>
                {formattedChargeDate && (
                  <div className={styles.breakdownRow}>
                    <span className={styles.breakdownLabel}>Charge date</span>
                    <span className={styles.breakdownAmount}>{formattedChargeDate}</span>
                  </div>
                )}
              </>
            )}
          </div>
          <BillingInfoCard.Body>Each upcoming charge is billed separately on its listed date.</BillingInfoCard.Body>
        </>
      )}
    </BillingInfoCard.Root>
  )
}
